import { useEffect, useRef, type CSSProperties } from "react";

const PARALLAX_AMPLITUDE = 28;

type PhotoPlaceholderProps = {
  alt?: string;
  caption?: string;
  height?: number;
  horizon?: number;
  objectPosition?: string;
  parallax?: boolean;
  showCaption?: boolean;
  sizes?: string;
  src?: string;
  vignette?: boolean;
  style?: CSSProperties;
};

export function PhotoPlaceholder({
  alt = "",
  caption = "low-sun road · paper-treated · no faces",
  height = 460,
  horizon = 0.62,
  objectPosition = "center",
  parallax = false,
  showCaption,
  sizes = "100vw",
  src,
  vignette = true,
  style,
}: PhotoPlaceholderProps) {
  const shouldShowCaption = showCaption ?? !src;
  const frameRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!parallax) return;
    const frame = frameRef.current;
    const layer = layerRef.current;
    if (!frame || !layer) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const center = rect.top + rect.height / 2;
      const span = vh / 2 + rect.height / 2;
      const progress = Math.max(-1, Math.min(1, (center - vh / 2) / span));
      layer.style.transform = `translate3d(0, ${(-progress * PARALLAX_AMPLITUDE).toFixed(1)}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [parallax]);

  const layerStyle: CSSProperties = parallax
    ? {
        position: "absolute",
        left: 0,
        right: 0,
        top: -PARALLAX_AMPLITUDE,
        height: `calc(100% + ${PARALLAX_AMPLITUDE * 2}px)`,
        willChange: "transform",
      }
    : { position: "absolute", inset: 0 };

  return (
    <div
      ref={frameRef}
      style={{
        position: "relative",
        width: "100%",
        height,
        borderRadius: 2,
        overflow: "hidden",
        background: `linear-gradient(180deg,
          color-mix(in oklab, var(--accent) 14%, var(--bg)) 0%,
          color-mix(in oklab, var(--accent) 22%, var(--bg)) ${horizon * 100 - 4}%,
          color-mix(in oklab, var(--text) 24%, var(--bg)) ${horizon * 100}%,
          color-mix(in oklab, var(--text) 34%, var(--bg)) 100%
        )`,
        boxShadow: "inset 0 0 0 0.5px var(--hairline-strong)",
        ...style,
      }}
    >
      <div ref={layerRef} style={layerStyle}>
        {src ? (
          <img
            src={src}
            alt={alt}
            sizes={sizes}
            loading="lazy"
            decoding="async"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition,
            }}
          />
        ) : (
          <>
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${horizon * 100}%`,
                height: 0.5,
                background: "rgba(28,26,23,0.35)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: "62%",
                top: `${horizon * 100 - 6}%`,
                width: 220,
                height: 220,
                borderRadius: "50%",
                background:
                  "radial-gradient(closest-side, color-mix(in oklab, var(--accent) 60%, #f4efe6) 0%, transparent 70%)",
                opacity: 0.7,
                filter: "blur(4px)",
                transform: "translate(-50%, -50%)",
              }}
            />
          </>
        )}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(28,26,23,0.018) 0 1px, transparent 1px 4px)",
          mixBlendMode: "multiply",
        }}
      />
      {vignette && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(120% 80% at 50% 60%, transparent 50%, rgba(28,26,23,0.18) 100%)",
          }}
        />
      )}
      {shouldShowCaption && (
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 14,
            fontFamily: "var(--mono)",
            fontSize: 10,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(244,239,230,0.7)",
          }}
        >
          photo · {caption}
        </div>
      )}
    </div>
  );
}
